/**
 * Attorney Analysis Section for Accountant Report View
 * AI-powered case builder from the ATTORNEY/CLAIMANT perspective
 * 
 * Purpose: Help plaintiff attorneys maximize client recovery by:
 * - Identifying unclaimed losses ("money left on the table")
 * - Finding critical vulnerabilities that need fixing
 * - Building stronger documentation
 * - Providing actionable case strategy
 * 
 * Based on the Attorney Case Builder methodology:
 * - Maximum Loss Calculation mindset
 * - Gap Analysis & Solutions
 * - Proactive Defense Preparation
 */

import React, { useState, useEffect } from 'react';
import axios from '@/lib/http';
import { SupersededAnalysisNote } from '@/components/shared/SupersededAnalysisNote';
import {
  Scale,
  TrendingUp,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  FileText,
  Target,
  Clock,
  Users,
  Briefcase
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface AttorneyAnalysisProps {
  reportId: string;
  getAuthHeader: () => { Authorization: string } | {};
  formatCurrency: (amount?: number) => string;
}

interface UnclaimedLoss {
  category: string;
  title: string;
  description: string;
  dollar_impact: number;
  why_recoverable: string;
  confidence: string;
  attorney_tasks?: string[];
  client_tasks?: string[];
  evidence_needed?: string[];
  insurance_response?: string;
  counter_argument?: string;
}

interface CriticalVulnerability {
  issue_number: number;
  title: string;
  severity: string;
  the_problem: string;
  why_it_matters: string;
  financial_risk: number;
  the_fix: {
    steps: string[];
    deadline: string;
    required_docs: string[];
  };
  fallback_position?: string;
}

interface SettlementStrategy {
  current_claim: number;
  potential_claim: number;
  additional_recovery: number;
  claim_strength: string;
  opening_demand: number;
  target_settlement: number;
  walk_away: number;
  leverage_points: string[];
  expected_insurance_response: string;
}

interface AttorneyAnalysis {
  success: boolean;
  cached: boolean;
  analysis_id?: string;
  analyzed_at?: string;

  // Executive Summary
  claim_amount?: number;
  potential_claim?: number;
  additional_recovery?: number;
  executive_summary?: string;
  case_strength?: string;
  settlement_probability?: string;

  // Unclaimed Losses
  unclaimed_losses?: UnclaimedLoss[];
  total_unclaimed?: number;

  // Critical Vulnerabilities
  vulnerabilities?: CriticalVulnerability[];
  total_risk?: number;

  // Settlement Strategy
  settlement_strategy?: SettlementStrategy;

  // Client Action Plan
  client_actions?: {
    immediate: string[];
    this_week: string[];
    next_week: string[];
  };

  // Recommendations
  recommendations?: string[];

  error?: string;

  currency?: {
    superseded?: boolean;
    current_run_id?: string | null;
    current_run_at?: string | null;
    analysis_at?: string | null;
  } | null;
}

export default function AttorneyAnalysisSection({
  reportId,
  getAuthHeader,
  formatCurrency
}: AttorneyAnalysisProps) {
  const [analysis, setAnalysis] = useState<AttorneyAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    unclaimed: true,
    vulnerabilities: true,
    strategy: false,
    actions: false
  });

  const runAnalysis = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/attorney/analyze/${reportId}?force_refresh=${forceRefresh}`,
        {},
        { headers: getAuthHeader() }
      );
      setAnalysis(response.data);
    } catch (err: any) {
      console.error('Attorney analysis failed:', err);
      setError(err.response?.data?.detail || 'Failed to run attorney analysis');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/attorney/analysis/${reportId}`,
        { headers: getAuthHeader() }
      );
      setAnalysis(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setAnalysis(null);
      } else {
        console.error('Attorney analysis fetch failed:', err);
        setError(err.response?.data?.detail || 'Failed to load attorney analysis');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
  }, [reportId]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getCaseStrengthColor = (strength?: string): React.CSSProperties => {
    switch (strength?.toUpperCase()) {
      case 'STRONG': return { backgroundColor: 'transparent', color: 'var(--color-verified)', borderColor: 'var(--color-verified)' };
      case 'MODERATE': return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' };
      case 'WEAK': return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' };
      case 'AT_RISK': return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' };
      default: return { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', borderColor: 'var(--color-border)' };
    }
  };

  // Severity = fill-strength ladder in ONE hue (K3 law §2: solid→tint→outline→ghost), not
  // four identical orange borders. critical carries the orange tint fill+border (act-now); high
  // drops the fill to a solid orange outline; medium falls to a neutral hairline; low is a ghost
  // sub-border at dim ink. No orange-wash across tiers; the retired status tokens are gone.
  const getSeverityColor = (severity?: string): React.CSSProperties => {
    switch (severity?.toLowerCase()) {
      case 'critical': return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-text-primary)' }; // design-law-allow: K3-prescribed act-now tint tier
      case 'high': return { backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-brand)', color: 'var(--color-text-primary)' };
      case 'medium': return { backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' };
      case 'low': return { backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border-sub)', color: 'var(--color-text-dim)' };
      default: return { backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' };
    }
  };

  const getConfidenceColor = (confidence?: string): React.CSSProperties => {
    switch (confidence?.toLowerCase()) {
      case 'high': return { color: 'var(--color-verified)' };
      case 'medium': return { color: 'var(--color-brand)' };
      case 'low': return { color: 'var(--color-text-muted)' };
      default: return { color: 'var(--color-text-muted)' };
    }
  };

  if (loading && !analysis) {
    return (
      <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Scale className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
          <h2 className="cs-h2">Attorney Analysis</h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 mb-4" style={{ borderColor: 'var(--color-verified)' }}></div>
            <p style={{ color: 'var(--color-text-muted)' }}>Building your case...</p>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>Analyzing for maximum recovery</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Scale className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
          <h2 className="cs-h2">Attorney Analysis</h2>
        </div>
        <div className="border border-[var(--color-border)] rounded-md p-4 bg-[var(--color-bg-elevated)]">
          <p style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
          <button
            onClick={() => runAnalysis(true)}
            className="mt-3 px-4 py-2 rounded-md text-sm"
            style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
          >
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Scale className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
          <h2 className="cs-h2">Attorney Analysis</h2>
        </div>
        <div className="py-12 text-center">
          <Scale className="mx-auto h-8 w-8 text-[var(--color-text-ghost)]" />
          <p className="mt-3 text-sm text-[var(--color-text-muted)]">
            No attorney analysis available yet.
          </p>
          <button
            onClick={() => runAnalysis(false)}
            disabled={loading}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm"
            style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Run attorney analysis
          </button>
        </div>
      </div>
    );
  }

  const {
    executive_summary,
    case_strength,
    settlement_probability,
    claim_amount,
    potential_claim,
    additional_recovery,
    unclaimed_losses,
    total_unclaimed,
    vulnerabilities,
    total_risk,
    settlement_strategy,
    client_actions,
    recommendations,
    cached,
    analyzed_at,
    currency
  } = analysis;

  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6 mb-6" data-testid="attorney-analysis-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md" style={{ backgroundColor: 'transparent' }}>
            <Scale className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
          </div>
          <div>
            <h2 className="cs-h2">Attorney Analysis</h2>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {cached ? '(Cached)' : 'Fresh analysis'} • {analyzed_at ? new Date(analyzed_at).toLocaleString() : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => runAnalysis(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-md transition-colors border border-[var(--color-border)] text-[var(--color-brand)] hover:bg-[var(--color-bg-hover)]"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Re-analyze
        </button>
      </div>

      {/* Superseded analysis notice */}
      <SupersededAnalysisNote currency={currency} />

      {/* Executive Summary */}
      <div className="rounded-md p-6 mb-6 border" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="cs-h3 mb-2">Case Analysis Summary</h3>
            <p style={{ color: 'var(--color-text-secondary)' }}>{executive_summary}</p>
          </div>
          <div className="flex flex-col items-end gap-2 ml-4">
            <span className="px-3 py-1.5 rounded-full text-sm font-semibold border" style={getCaseStrengthColor(case_strength)}>
              {case_strength?.replace('_', ' ')} CASE
            </span>
            {settlement_probability && (
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {settlement_probability} settlement probability
              </span>
            )}
          </div>
        </div>

        {/* Financial Summary */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t" style={{ borderColor: 'var(--color-verified)' }}>
          <div className="text-center">
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Current Claim</p>
            <p className="text-lg font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-text-secondary)' }}>
              {formatCurrency(claim_amount)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Potential Claim</p>
            <p className="text-lg font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-verified)' }}>
              {formatCurrency(potential_claim)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Additional Recovery</p>
            <p className="text-lg font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-verified)' }}>
              +{formatCurrency(additional_recovery)}
            </p>
          </div>
        </div>
      </div>

      {/* Unclaimed Losses - Money on the Table */}
      {unclaimed_losses && unclaimed_losses.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('unclaimed')}
            className="w-full flex items-center justify-between p-4 rounded-md transition-colors"
            style={{ backgroundColor: 'transparent' }}
          >
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>
                Unclaimed Losses: {formatCurrency(total_unclaimed)} on the Table
              </span>
            </div>
            {expandedSections.unclaimed ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.unclaimed && (
            <div className="mt-3 space-y-4">
              {unclaimed_losses.map((loss, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-md border"
                  style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <TrendingUp className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-verified)' }} />
                      <div>
                        <h4 className="font-semibold" style={{ color: 'var(--color-verified)' }}>{loss.title}</h4>
                        <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>{loss.category}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>+{formatCurrency(loss.dollar_impact)}</p>
                      <span className="text-xs font-semibold" style={getConfidenceColor(loss.confidence)}>
                        {loss.confidence} confidence
                      </span>
                    </div>
                  </div>

                  <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>{loss.description}</p>

                  <div className="rounded p-3 mb-3 bg-[var(--color-bg-elevated)]">
                    <p className="text-sm font-semibold mb-1" style={{ color: 'var(--color-verified)' }}>Why This is Recoverable:</p>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{loss.why_recoverable}</p>
                  </div>

                  {loss.attorney_tasks && loss.attorney_tasks.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-text-muted)' }}>Attorney Tasks:</p>
                      <ul className="text-xs space-y-1" style={{ color: 'var(--color-text-muted)' }}>
                        {loss.attorney_tasks.map((task, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span style={{ color: 'var(--color-verified)' }}>→</span> {task}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {loss.client_tasks && loss.client_tasks.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-text-muted)' }}>Client Tasks:</p>
                      <ul className="text-xs space-y-1" style={{ color: 'var(--color-text-muted)' }}>
                        {loss.client_tasks.map((task, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span style={{ color: 'var(--color-needs-human-text)' }}>☐</span> {task}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {loss.insurance_response && (
                    <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-verified)' }}>
                      <p className="text-xs">
                        <span className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>Expected Insurance Response:</span>{' '}
                        <span style={{ color: 'var(--color-text-muted)' }}>{loss.insurance_response}</span>
                      </p>
                      {loss.counter_argument && (
                        <p className="text-xs mt-1">
                          <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>Counter-Argument:</span>{' '}
                          <span style={{ color: 'var(--color-text-muted)' }}>{loss.counter_argument}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Critical Vulnerabilities - Fix These Now */}
      {vulnerabilities && vulnerabilities.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('vulnerabilities')}
            className="w-full flex items-center justify-between p-4 rounded-md transition-colors bg-[var(--color-bg-elevated)]"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Critical Vulnerabilities ({vulnerabilities.length}) - Fix These Now
              </span>
            </div>
            {expandedSections.vulnerabilities ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.vulnerabilities && (
            <div className="mt-3 space-y-4">
              {vulnerabilities.map((vuln, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-md border-l-4"
                  style={getSeverityColor(vuln.severity)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
                      <div>
                        <h4 className="font-semibold">Critical Issue #{vuln.issue_number}: {vuln.title}</h4>
                        <span className="text-xs px-2 py-0.5 rounded uppercase" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>{vuln.severity}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>Risk: {formatCurrency(vuln.financial_risk)}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="rounded p-3 bg-[var(--color-bg-elevated)]">
                      <p className="text-sm font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>The Problem:</p>
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{vuln.the_problem}</p>
                    </div>

                    <div className="rounded p-3 bg-[var(--color-bg-elevated)]">
                      <p className="text-sm font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>Why This Matters:</p>
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{vuln.why_it_matters}</p>
                    </div>

                    <div className="rounded p-3 border" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
                      <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-verified)' }}>THE FIX:</p>
                      <ul className="text-sm space-y-1 mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                        {vuln.the_fix.steps.map((step, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="font-bold" style={{ color: 'var(--color-verified)' }}>{i + 1}.</span> {step}
                          </li>
                        ))}
                      </ul>
                      <div className="flex items-center gap-4 text-xs mt-2 pt-2 border-t" style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-verified)' }}>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Deadline: {vuln.the_fix.deadline}
                        </span>
                      </div>
                      {vuln.the_fix.required_docs.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-semibold" style={{ color: 'var(--color-text-muted)' }}>Required Documentation:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {vuln.the_fix.required_docs.map((doc, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 rounded bg-[var(--color-bg-elevated)]" style={{ color: 'var(--color-text-muted)' }}>{doc}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {vuln.fallback_position && (
                      <div className="rounded p-3 border bg-[var(--color-bg-elevated)] border-[var(--color-border)]">
                        <p className="text-xs font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>Fallback Position:</p>
                        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{vuln.fallback_position}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settlement Strategy */}
      {settlement_strategy && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('strategy')}
            className="w-full flex items-center justify-between p-4 rounded-md transition-colors bg-[var(--color-bg-elevated)]"
          >
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Settlement Strategy</span>
            </div>
            {expandedSections.strategy ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.strategy && (
            <div className="mt-3 rounded-md p-4 border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="rounded-md p-3 text-center bg-[var(--color-bg-elevated)]">
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Opening Demand</p>
                  <p className="text-lg font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(settlement_strategy.opening_demand)}</p>
                </div>
                <div className="rounded-md p-3 text-center bg-[var(--color-bg-elevated)]">
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Target Settlement</p>
                  <p className="text-lg font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-verified)' }}>{formatCurrency(settlement_strategy.target_settlement)}</p>
                </div>
                <div className="rounded-md p-3 text-center bg-[var(--color-bg-elevated)]">
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Walk Away</p>
                  <p className="text-lg font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(settlement_strategy.walk_away)}</p>
                </div>
              </div>

              {settlement_strategy.leverage_points && settlement_strategy.leverage_points.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Leverage Points:</p>
                  <ul className="space-y-1">
                    {settlement_strategy.leverage_points.map((point, idx) => (
                      <li key={idx} className="text-sm flex items-start gap-2" style={{ color: 'var(--color-text-muted)' }}>
                        <span style={{ color: 'var(--color-needs-human-text)' }}>✓</span> {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {settlement_strategy.expected_insurance_response && (
                <div className="rounded p-3 bg-[var(--color-bg-elevated)]">
                  <p className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-secondary)' }}>Expected Insurance Response:</p>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{settlement_strategy.expected_insurance_response}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Client Action Plan */}
      {client_actions && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('actions')}
            className="w-full flex items-center justify-between p-4 rounded-md transition-colors bg-[var(--color-bg-elevated)]"
          >
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Client Action Plan</span>
            </div>
            {expandedSections.actions ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.actions && (
            <div className="mt-3 rounded-md p-4 border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
              <div className="grid grid-cols-3 gap-4">
                {client_actions.immediate && client_actions.immediate.length > 0 && (
                  <div className="rounded-md p-3 bg-[var(--color-bg-elevated)]">
                    <p className="text-sm font-bold mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Immediate (24-48hrs)</p>
                    <ul className="space-y-1">
                      {client_actions.immediate.map((action, idx) => (
                        <li key={idx} className="text-xs flex items-start gap-1" style={{ color: 'var(--color-text-muted)' }}>
                          <span style={{ color: 'var(--color-needs-human-text)' }}>☐</span> {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {client_actions.this_week && client_actions.this_week.length > 0 && (
                  <div className="rounded-md p-3 bg-[var(--color-bg-elevated)]">
                    <p className="text-sm font-bold mb-2" style={{ color: 'var(--color-needs-human-text)' }}>This Week</p>
                    <ul className="space-y-1">
                      {client_actions.this_week.map((action, idx) => (
                        <li key={idx} className="text-xs flex items-start gap-1" style={{ color: 'var(--color-text-muted)' }}>
                          <span style={{ color: 'var(--color-brand)' }}>☐</span> {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {client_actions.next_week && client_actions.next_week.length > 0 && (
                  <div className="rounded-md p-3 bg-[var(--color-bg-elevated)]">
                    <p className="text-sm font-bold mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Next Week</p>
                    <ul className="space-y-1">
                      {client_actions.next_week.map((action, idx) => (
                        <li key={idx} className="text-xs flex items-start gap-1" style={{ color: 'var(--color-text-muted)' }}>
                          <span style={{ color: 'var(--color-needs-human-text)' }}>☐</span> {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="border rounded-md p-4" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
          <h3 className="cs-h3 mb-3 flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
            <Briefcase className="w-5 h-5" />
            Case Strategy Recommendations
          </h3>
          <ul className="space-y-2">
            {recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: 'var(--color-verified)' }}>
                <span className="mt-1" style={{ color: 'var(--color-verified)' }}>→</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
